import { OrderResponseItem } from '../../../data/models'
import {
  ComparisonArray,
  areItemsModified,
} from '../../../utils/helper/checkConfirmationCodes'
import { FormValues } from '../components/Positions/dialogs/Edit/Edit'

/**
 * Determines the confirmation code for an order based on whether the items have been modified.
 *
 * @param {FormValues} updatedOrder - The updated order data from the form.
 * @param {OrderResponseItem} originalOrder - The original order data.
 * @param {OrderResponseItem['ConfirmationCode']} confirmationCode - The current confirmation code of the order.
 * @returns {OrderResponseItem['ConfirmationCode']} - Returns 'REJECTED' if the confirmation code is 'REJECTED',
 * otherwise returns 'MODIFIED' if items have been modified, or 'NOT_SET' if items have not been modified.
 */
export const getConfirmationCode = (
  updatedOrder: FormValues | OrderResponseItem,
  originalOrder?: OrderResponseItem,
  confirmationCode?: OrderResponseItem['ConfirmationCode'],
): OrderResponseItem['ConfirmationCode'] => {
  if (confirmationCode === 'REJECTED') return 'REJECTED'

  const comparisionArray: ComparisonArray = [
    {
      order: originalOrder?.ItemId,
      confirmation:
        (updatedOrder as FormValues).confirmationItemId ??
        (updatedOrder as OrderResponseItem).ConfirmationItemId,
    },
    {
      order: originalOrder?.CountryOfOrigin,
      confirmation:
        (updatedOrder as FormValues).confirmationCountryOfOrigin ??
        (updatedOrder as OrderResponseItem).ConfirmationCountryOfOrigin,
    },
    // TODO: The comparision of this fields makes problems:
    // - SellerProductId and ConfirmationSellerProductId
    // - SellerProductDescription and ConfirmationSellerProductDescription
    // It can happen that SellerProductId and SellerProductDescription are not filled with data while the fields
    // ConfirmationSellerProductId and ConfirmationSellerProductDescription are filled with data.
    // Than the comparision fails and says the item is modified!
    // Also this fields are not visible for editing because are disabled in config!
    // {
    //   order: originalOrder?.SellerProductId,
    //   confirmation:
    //     (updatedOrder as FormValues).confirmationSellerProductId ??
    //     (updatedOrder as OrderResponseItem).ConfirmationSellerProductId,
    // },
    // {
    //   order: originalOrder?.SellerProductDescription,
    //   confirmation:
    //     (updatedOrder as FormValues).confirmationSellerProductDescription ??
    //     (updatedOrder as OrderResponseItem)
    //       .ConfirmationSellerProductDescription,
    // },
    {
      order: originalOrder?.OrderQuantity,
      confirmation:
        (updatedOrder as FormValues).confirmationQuantity ??
        (updatedOrder as OrderResponseItem).ConfirmationQuantity,
    },
    {
      order: originalOrder?.OrderUnit,
      confirmation:
        (updatedOrder as FormValues).confirmationUnit ??
        (updatedOrder as OrderResponseItem).ConfirmationUnit,
    },
    {
      order: originalOrder?.OrderPriceBaseQuantity,
      confirmation:
        (updatedOrder as FormValues).confirmationPriceBaseQuantity ??
        (updatedOrder as OrderResponseItem).ConfirmationPriceBaseQuantity,
    },
    {
      order: originalOrder?.OrderPriceUnitOfMeasure,
      confirmation:
        (updatedOrder as FormValues).confirmationPriceUnitOfMeasure ??
        (updatedOrder as OrderResponseItem).ConfirmationPriceUnitOfMeasure,
    },
    {
      order: originalOrder?.OrderPrice,
      confirmation:
        (updatedOrder as FormValues).confirmationPrice ??
        (updatedOrder as OrderResponseItem).ConfirmationPrice,
    },
    {
      order: originalOrder?.OrderPriceSum,
      confirmation:
        (updatedOrder as FormValues).confirmationPriceSum ??
        (updatedOrder as OrderResponseItem).ConfirmationPriceSum,
    },
    {
      order: originalOrder?.AllowanceChargeSum,
      confirmation:
        (updatedOrder as FormValues).confirmationAllowanceChargeSum ??
        (updatedOrder as OrderResponseItem).ConfirmationAllowanceChargeSum,
    },
    {
      order: originalOrder?.LineItemAmount,
      confirmation:
        (updatedOrder as FormValues).confirmationLineItemAmount ??
        (updatedOrder as OrderResponseItem).ConfirmationLineItemAmount,
    },
    {
      order: new Date(originalOrder?.OrderDeliveryDate!).toDateString(),
      confirmation: (updatedOrder as FormValues).confirmationDeliveryDate
        ? (updatedOrder as FormValues).confirmationDeliveryDate!.toDateString()
        : new Date(
            (updatedOrder as OrderResponseItem).ConfirmationDeliveryDate!,
          ).toDateString(),
    },
  ]

  return areItemsModified(comparisionArray) ? 'MODIFIED' : 'NOT_SET'
}
